import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  IoSearchOutline,
  IoChevronDown,
  IoClose,
  IoCloudUploadOutline,
  IoSyncOutline,
  IoRocketOutline,
  IoImageOutline,
  IoAlbumsOutline,
  IoPersonOutline,
  IoWalletOutline,
  IoChatbubblesOutline,
  IoHelpCircleOutline,
} from "react-icons/io5";
import { creatorPublicSupportCategories } from "../../api/HttpApis";
import Button from "../../component/button/Button";
import style from "./CreatorFaq.module.css";
import { FAQ_SECTIONS } from "./faqData";

const SUPPORT_PATH = "/mainlayout/creator-support";
// Logged out (Help Center, /creator-help): the login page's Contact Support form.
const PUBLIC_SUPPORT_PATH = "/creator-login?contact=1";

const SECTION_ICONS = {
  upload: <IoCloudUploadOutline />,
  processing: <IoSyncOutline />,
  publishing: <IoRocketOutline />,
  artwork: <IoImageOutline />,
  series: <IoAlbumsOutline />,
  account: <IoPersonOutline />,
  earnings: <IoWalletOutline />,
  support: <IoChatbubblesOutline />,
};

const normalize = (s) => String(s || "").toLowerCase();

// Card columns. Cards are dealt into columns here (not with CSS columns or
// grid) so opening a question only makes its own card taller: CSS columns
// rebalance and move cards between columns, and grid rows stretch the card
// beside it.
const COLUMN_MIN_WIDTH = 420;
const COLUMN_GAP = 16;

const useColumnCount = (ref) => {
  const [count, setCount] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const width = el.clientWidth;
      setCount(
        Math.max(
          1,
          Math.floor((width + COLUMN_GAP) / (COLUMN_MIN_WIDTH + COLUMN_GAP)),
        ),
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return count;
};

// Shortest-column-first by question count (closed height), so the columns
// come out roughly even and the order reads left-to-right.
const distribute = (sections, columns) => {
  const cols = Array.from({ length: columns }, () => ({
    items: [],
    weight: 0,
  }));
  sections.forEach((s) => {
    const target = cols.reduce(
      (min, c) => (c.weight < min.weight ? c : min),
      cols[0],
    );
    target.items.push(s);
    target.weight += s.items.length + 1.5; // + header
  });
  return cols.map((c) => c.items);
};

/**
 * Creator FAQ, linked under Help & Support in the sidebar and, with `isPublic`,
 * as the logged-out Help Center from the creator login page. Static content
 * (faqData.js); sections marked `requires: "earnings"` only show where creator
 * monetization exists, which the backend signals by including the "earnings"
 * category in GET /creators/support/public-categories.
 */
const CreatorFaq = ({ isPublic = false }) => {
  const supportPath = isPublic ? PUBLIC_SUPPORT_PATH : SUPPORT_PATH;
  const [query, setQuery] = useState("");
  const [section, setSection] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [hasEarnings, setHasEarnings] = useState(false);

  useEffect(() => {
    let alive = true;
    creatorPublicSupportCategories()
      .then((res) => {
        const list = res?.data?.data;
        if (alive && Array.isArray(list))
          setHasEarnings(list.some((c) => c.key === "earnings"));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const sections = useMemo(
    () =>
      FAQ_SECTIONS.filter(
        (s) => !s.requires || (s.requires === "earnings" && hasEarnings),
      ),
    [hasEarnings],
  );

  const q = normalize(query.trim());
  const visible = useMemo(
    () =>
      sections
        .filter((s) => section === "all" || s.id === section)
        .map((s) => ({
          ...s,
          items: q
            ? s.items.filter((i) =>
                normalize(`${i.q} ${i.a.join(" ")}`).includes(q),
              )
            : s.items,
        }))
        .filter((s) => s.items.length > 0),
    [sections, section, q],
  );
  const resultCount = visible.reduce((n, s) => n + s.items.length, 0);

  const gridRef = useRef(null);
  const columnCount = useColumnCount(gridRef);
  const columns = useMemo(
    () =>
      distribute(visible, Math.min(columnCount, Math.max(1, visible.length))),
    [visible, columnCount],
  );

  return (
    <div className={style.page}>
      <div className={style.header}>
        <h1 className={style.title}>Frequently asked questions</h1>
        <p className={style.subtitle}>
          Quick answers about uploading, publishing and your account. Can't find
          what you need?{" "}
          <Link to={supportPath} className={style.inlineLink}>
            Contact support
          </Link>
          .
        </p>
      </div>

      <div className={style.searchWrap}>
        <IoSearchOutline className={style.searchIcon} />
        <input
          type="search"
          className={style.search}
          placeholder="Search questions, e.g. “upload stuck”"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpenId(null);
          }}
          aria-label="Search the FAQ"
        />
        {query && (
          <button
            type="button"
            className={style.clear}
            onClick={() => setQuery("")}
            aria-label="Clear search"
          >
            <IoClose />
          </button>
        )}
      </div>

      <div className={style.chips} role="tablist" aria-label="FAQ topics">
        {[{ id: "all", title: "All topics" }, ...sections].map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={section === s.id}
            className={`${style.chip} ${section === s.id ? style.chipActive : ""}`}
            onClick={() => {
              setSection(s.id);
              setOpenId(null);
            }}
          >
            {s.title}
          </button>
        ))}
      </div>

      {resultCount === 0 ? (
        <div className={style.empty}>
          <p>No questions match “{query.trim()}”.</p>
          <Button variant="outline" onClick={() => setQuery("")}>
            Clear search
          </Button>
        </div>
      ) : null}
      <div
        ref={gridRef}
        className={style.grid}
        style={{ display: resultCount === 0 ? "none" : undefined }}
      >
        {columns.map((col, ci) => (
          <div key={ci} className={style.column}>
            {col.map((s) => (
              <section key={s.id} className={style.card}>
                <header className={style.cardHead}>
                  <span className={style.cardIcon}>
                    {SECTION_ICONS[s.id] || <IoHelpCircleOutline />}
                  </span>
                  <h2 className={style.cardTitle}>{s.title}</h2>
                  <span className={style.cardCount}>{s.items.length}</span>
                </header>
                <ul className={style.list}>
                  {s.items.map((item, idx) => {
                    const id = `${s.id}-${idx}`;
                    const open = openId === id || (q && resultCount <= 3);
                    return (
                      <li
                        key={id}
                        className={`${style.item} ${open ? style.itemOpen : ""}`}
                      >
                        <button
                          type="button"
                          className={style.question}
                          onClick={() => setOpenId(openId === id ? null : id)}
                          aria-expanded={open}
                          aria-controls={`faq-${id}`}
                        >
                          <span>{item.q}</span>
                          <IoChevronDown className={style.chev} />
                        </button>
                        {open && (
                          <div id={`faq-${id}`} className={style.answer}>
                            {item.a.map((p, i) =>
                              Array.isArray(p) ? (
                                <ul key={i}>
                                  {p.map((li, j) => (
                                    <li key={j}>{li}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p key={i}>{p}</p>
                              ),
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        ))}
      </div>

      <div className={style.cta}>
        <div>
          <h2 className={style.ctaTitle}>Still stuck?</h2>
          <p className={style.ctaText}>
            {isPublic
              ? "Send us a message and our team will reply by email."
              : "Send us a request with a screenshot and our team will reply by email."}
          </p>
        </div>
        <Link
          to={supportPath}
          state={isPublic ? undefined : { from: "/mainlayout/creator-faq" }}
          className={style.ctaBtn}
        >
          Contact support
        </Link>
      </div>
    </div>
  );
};

export default CreatorFaq;
